import { useEffect, useId, useState } from 'react';
import { getIntygUrl, uploadIntyg } from '../lib/storage';

interface Props {
  boatId: string;
  label: string;
  value: string | null;
  onChange: (fileRef: string | null) => void;
}

const UUID_PREFIX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}-/;

function displayName(fileRef: string): string {
  const last = fileRef.split('/').pop() ?? fileRef;
  return last.replace(UUID_PREFIX, '');
}

function isImage(fileRef: string): boolean {
  return /\.(jpe?g|png|webp|heic|heif)$/i.test(fileRef);
}

export default function FileUploadField({ boatId, label, value, onChange }: Props) {
  const id = useId();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!value) {
      setPreviewUrl(null);
      return;
    }
    let cancelled = false;
    getIntygUrl(value)
      .then((url) => {
        if (!cancelled) setPreviewUrl(url);
      })
      .catch(() => {
        if (!cancelled) setPreviewUrl(null);
      });
    return () => {
      cancelled = true;
    };
  }, [value]);

  async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setErr(null);
    setBusy(true);
    try {
      const ref = await uploadIntyg(boatId, file);
      onChange(ref);
    } catch (caught) {
      setErr(caught instanceof Error ? caught.message : 'Uppladdning misslyckades.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-1">
      <label htmlFor={id} className="block text-sm font-medium">
        {label}
      </label>
      <input
        id={id}
        type="file"
        accept="application/pdf,image/*"
        onChange={handleFile}
        disabled={busy}
        className="block text-sm"
      />
      {value && (
        <div className="text-xs text-gray-700 space-y-1">
          <p>
            Uppladdad fil:{' '}
            <code className="text-xs">{displayName(value)}</code>
          </p>
          {previewUrl &&
            (isImage(value) ? (
              <img
                src={previewUrl}
                alt={displayName(value)}
                className="max-h-32 rounded border"
              />
            ) : (
              <a
                href={previewUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sky underline"
              >
                Öppna fil
              </a>
            ))}
        </div>
      )}
      {busy && <p className="text-xs text-gray-500">Laddar upp…</p>}
      {err && <p className="text-bad text-sm">{err}</p>}
    </div>
  );
}
